import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { Button } from "@primitives/button";
import { Input } from "@primitives/input";
import { Checkbox } from "@primitives/checkbox";
import TermsModal from "@/components/shared/TermsModal/TermsModal";

import { useState } from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { logoutUser, registerUser } from "@/middleware/auth";
import { useNavigate } from "react-router-dom";
import { RegisterFormSchema, RegisterFormType } from "@/schemas/auth";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";
import { MAX_PASSWORD_LENGTH } from "@/config/constants";
import { ApiError } from "@/server/apiError";
import { useTurnstile } from "./hooks/useTurnstile";

interface Props {
  verifiedEmail: string;
  onVerificationExpired: () => void;
}

export default function RegisterForm({
  verifiedEmail,
  onVerificationExpired,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tcOpen, setTcOpen] = useState(false);
  const {
    ref,
    token,
    isReady,
    showError,
    enabled: turnstileEnabled,
    status,
    siteKey,
    onSuccess,
    onError,
    onExpire,
    onBeforeInteractive,
    onAfterInteractive,
    reset,
  } = useTurnstile();

  const form = useForm<RegisterFormType>({
    resolver: zodResolver(RegisterFormSchema),
    defaultValues: {
      username: "",
      password: "",
      confirmPassword: "",
      acceptedTc: false,
    },
  });

  const acceptedTc = form.watch("acceptedTc");

  async function onSubmit(values: RegisterFormType) {
    try {
      const result = await registerUser(
        values,
        verifiedEmail,
        token ?? undefined,
      );
      // TC-acceptance gate: registerUser set up the pending-auth bundle and
      // opened the T&C dialog — do NOT navigate to ChatPage yet.
      if (result?.pendingTc) {
        return;
      }
      navigate("/", { replace: true });
    } catch (error) {
      logoutUser();

      if (error instanceof ApiError) {
        const code = error.data?.title;

        if (code === "errors.EmailVerification.NotVerified") {
          onVerificationExpired();
          return;
        }

        toast({
          duration: 4000,
          // Array form: fall back to the generic message when the title has
          // no translation, instead of rendering the raw key.
          description: t([code ?? "errorOccurred", "errorOccurred"]),
          variant: "destructive",
        });
      } else {
        let message =
          error instanceof Error ? error.message : t("errorOccurred");
        try {
          const parsed = JSON.parse(message) as { title?: string };
          if (parsed.title) {
            message = t(`errors.${parsed.title}`);
          }
        } catch {
          // not JSON — use message as-is
        }
        toast({
          duration: 4000,
          description: message,
          variant: "destructive",
        });
      }
    } finally {
      reset();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="my-7 space-y-3">
        <div className="flex flex-col gap-5">
          <Input
            className="rounded-lg border-border bg-card py-6 opacity-60"
            value={verifiedEmail}
            readOnly
            type="email"
            autoComplete="email"
            aria-label={t("register.emailStep.emailPlaceholder")}
          />

          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                    placeholder={t("register.username")}
                    autoComplete="username"
                    autoCapitalize="off"
                    autoCorrect="off"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value.toLowerCase());
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input
                      className="rounded-lg border-border bg-card py-6 pr-9 placeholder:opacity-70"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("register.password")}
                      autoComplete="new-password"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={MAX_PASSWORD_LENGTH}
                      {...field}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={t("register.togglePasswordVisibility")}
                    >
                      {showPassword ? (
                        <EyeIcon className="size-5 text-foreground/50" />
                      ) : (
                        <EyeOffIcon className="size-5 text-foreground/50" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input
                      className="rounded-lg border-border bg-card py-6 pr-9 placeholder:opacity-70"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t("register.confirmPassword")}
                      autoComplete="new-password"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={MAX_PASSWORD_LENGTH}
                      {...field}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      aria-label={t("register.toggleConfirmPasswordVisibility")}
                    >
                      {showConfirmPassword ? (
                        <EyeIcon className="size-5 text-foreground/50" />
                      ) : (
                        <EyeOffIcon className="size-5 text-foreground/50" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="acceptedTc"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-start gap-2">
                  <FormControl>
                    <Checkbox
                      id="acceptedTc"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-0.5"
                    />
                  </FormControl>
                  <span className="text-sm leading-tight text-muted-foreground">
                    <label htmlFor="acceptedTc" className="cursor-pointer">
                      {t("register.acceptTerms")}
                    </label>
                    <button
                      type="button"
                      className="text-secondary underline"
                      onClick={() => setTcOpen(true)}
                    >
                      {t("register.termsLink")}
                    </button>
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {turnstileEnabled && (
          <Turnstile
            className="[&>div>iframe]:!w-full [&>div]:!w-full"
            style={{ width: "100%" }}
            ref={ref}
            siteKey={siteKey}
            options={{ appearance: "interaction-only", size: "flexible" }}
            onSuccess={onSuccess}
            onError={onError}
            onExpire={onExpire}
            onBeforeInteractive={onBeforeInteractive}
            onAfterInteractive={onAfterInteractive}
          />
        )}

        {showError && (
          <p role="alert" className="text-sm text-destructive">
            {t("errors.Turnstile.Blocked")}
          </p>
        )}

        <div>
          <Button
            disabled={form.formState.isSubmitting || !acceptedTc || !isReady}
            type="submit"
            className="mt-10 h-12 w-full rounded-lg"
          >
            {form.formState.isSubmitting
              ? t("register.creatingAccount")
              : status === "loading"
                ? t("register.verifying")
                : t("register.title")}
          </Button>
        </div>
      </form>

      <TermsModal open={tcOpen} onOpenChange={setTcOpen} />
    </Form>
  );
}
